import React from 'react';

import { useLocale } from '@shared/hooks/locale';

type TranslateOptions = { count?: null | number } & Record<
  string,
  null | string | number | React.ReactNode
>;

export type TranslateWithComponents = (
  text: string,
  options?: TranslateOptions,
) => React.ReactNode;

export const useTranslationWithComponents = (): TranslateWithComponents => {
  const { t: translate } = useLocale();

  return (text, options) => {
    const componentsMap = new Map<string, React.ReactNode>();

    const optionsWithPlaceholders = Object.fromEntries(
      Object.entries(options ?? {}).map(([key, value]) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return [key, value];
        }

        componentsMap.set(key, value);
        return [key, `__{${key}}__`];
      }),
    );

    const textWithPlaceholders = translate(text, optionsWithPlaceholders);

    // Simple string replacement approach
    let result: React.ReactNode[] = [textWithPlaceholders];

    for (const [key, component] of componentsMap.entries()) {
      const placeholder = `__{${key}}__`;
      const newResult: React.ReactNode[] = [];

      for (const item of result) {
        if (typeof item === 'string') {
          const parts = item.split(placeholder);
          for (let i = 0; i < parts.length; i++) {
            if (i > 0) {
              newResult.push(component);
            }
            if (parts[i]) {
              newResult.push(parts[i]);
            }
          }
        } else {
          newResult.push(item);
        }
      }

      result = newResult;
    }

    return React.createElement('span', null, ...result);
  };
};
